import { useState, useEffect } from 'react'
import {
  Stack,
  Text,
  SegmentedControl,
  Textarea,
  Button,
  Group,
  Badge,
  Modal,
  TextInput,
  NumberInput,
  ActionIcon,
  Collapse,
  Checkbox,
  Image,
  Box,
  Center,
} from '@mantine/core'
import {
  IconCheck,
  IconX,
  IconDeviceFloppy,
  IconPlus,
  IconTrash,
  IconEdit,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { supabase } from '../../lib/supabase'
import { useSiteContent } from '@/hooks/useSiteContent'
import type { GuestWithDetails, AdditionalGuest } from '../../lib/types'
import styles from './RsvpForm.module.css'

interface RsvpFormProps {
  guest: GuestWithDetails
  onUpdate?: () => void
}

export function RsvpForm({ guest, onUpdate }: RsvpFormProps) {
  const { content } = useSiteContent()

  const [rsvpStatus, setRsvpStatus] = useState<'coming' | 'not_coming'>(
    guest.rsvp_status === 'not_coming' ? 'not_coming' : 'coming'
  )
  const [dietaryVegan, setDietaryVegan] = useState(guest.dietary_vegan || false)
  const [dietaryGlutenFree, setDietaryGlutenFree] = useState(guest.dietary_gluten_free || false)
  const [dietaryLactoseFree, setDietaryLactoseFree] = useState(guest.dietary_lactose_free || false)
  const [dietaryAllergies, setDietaryAllergies] = useState(guest.dietary_allergies || '')
  const [dietaryMisc, setDietaryMisc] = useState(guest.dietary_misc || '')
  const [comments, setComments] = useState(guest.comments || '')
  const [email, setEmail] = useState(guest.email || '')
  const [additionalGuests, setAdditionalGuests] = useState<AdditionalGuest[]>(
    guest.additional_guests || []
  )

  const [plusOneModalOpen, setPlusOneModalOpen] = useState(false)
  const [childModalOpen, setChildModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [guestToDelete, setGuestToDelete] = useState<{ id: string; name: string } | null>(null)
  const [plusOneName, setPlusOneName] = useState('')
  const [childName, setChildName] = useState('')
  const [childAge, setChildAge] = useState<number | undefined>(undefined)
  const [editingGuest, setEditingGuest] = useState<AdditionalGuest | null>(null)

  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setAdditionalGuests(guest.additional_guests || [])
  }, [guest])

  const refreshAdditionalGuests = async () => {
    const { data } = await supabase
      .from('additional_guests')
      .select('*')
      .eq('guest_id', guest.id)
      .order('added_date', { ascending: true })

    if (data) {
      setAdditionalGuests(data)
    }
  }

  const handleAddPlusOne = async () => {
    if (!plusOneName.trim()) return

    if (editingGuest) {
      // Update existing guest
      const { error } = await supabase
        .from('additional_guests')
        .update({ name: plusOneName.trim() })
        .eq('id', editingGuest.id)

      if (!error) {
        notifications.show({
          title: 'Erfolgreich',
          message: 'Begleitperson wurde aktualisiert',
          color: 'green',
        })
        setPlusOneName('')
        setEditingGuest(null)
        setPlusOneModalOpen(false)
        await refreshAdditionalGuests()
        onUpdate?.()
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Begleitperson konnte nicht aktualisiert werden',
          color: 'red',
        })
      }
    } else {
      // Add new guest
      const { error } = await supabase.from('additional_guests').insert({
        guest_id: guest.id,
        type: 'plus_one',
        name: plusOneName.trim(),
        age: null,
        added_by: 'guest',
      })

      if (!error) {
        notifications.show({
          title: 'Erfolgreich',
          message: 'Begleitperson wurde hinzugefügt',
          color: 'green',
        })
        setPlusOneName('')
        setPlusOneModalOpen(false)
        await refreshAdditionalGuests()
        onUpdate?.()
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Begleitperson konnte nicht hinzugefügt werden',
          color: 'red',
        })
      }
    }
  }

  const handleAddChild = async () => {
    if (!childName.trim() || childAge === undefined) return

    if (editingGuest) {
      // Update existing child
      const { error } = await supabase
        .from('additional_guests')
        .update({ name: childName.trim(), age: childAge })
        .eq('id', editingGuest.id)

      if (!error) {
        notifications.show({
          title: 'Erfolgreich',
          message: 'Kind wurde aktualisiert',
          color: 'green',
        })
        setChildName('')
        setChildAge(undefined)
        setEditingGuest(null)
        setChildModalOpen(false)
        await refreshAdditionalGuests()
        onUpdate?.()
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Kind konnte nicht aktualisiert werden',
          color: 'red',
        })
      }
      return
    }

    if (childAge < 0 || childAge > 17) {
      notifications.show({
        title: 'Fehler',
        message: 'Alter muss zwischen 0 und 17 liegen',
        color: 'red',
      })
      return
    }

    const { error } = await supabase.from('additional_guests').insert({
      guest_id: guest.id,
      type: 'child',
      name: childName.trim(),
      age: childAge,
      added_by: 'guest',
    })

    if (!error) {
      notifications.show({
        title: 'Erfolgreich',
        message: 'Kind wurde hinzugefügt',
        color: 'green',
      })
      setChildName('')
      setChildAge(undefined)
      setChildModalOpen(false)
      await refreshAdditionalGuests()
      onUpdate?.()
    } else {
      notifications.show({
        title: 'Fehler',
        message: 'Kind konnte nicht hinzugefügt werden',
        color: 'red',
      })
    }
  }

  const handleDeleteGuest = async () => {
    if (!guestToDelete) return

    const { error } = await supabase.from('additional_guests').delete().eq('id', guestToDelete.id)

    if (!error) {
      notifications.show({
        title: 'Erfolgreich',
        message: 'Zusätzlicher Gast wurde entfernt',
        color: 'green',
      })
      setDeleteModalOpen(false)
      setGuestToDelete(null)
      await refreshAdditionalGuests()
      onUpdate?.()
    } else {
      notifications.show({
        title: 'Fehler',
        message: 'Zusätzlicher Gast konnte nicht entfernt werden',
        color: 'red',
      })
    }
  }

  const sendRsvpNotification = async () => {
    try {
      await supabase.functions.invoke('rsvp-notification', {
        body: {
          guestName: guest.primary_name,
          guestEmail: email || null,
          rsvpStatus,
          dietaryInfo: rsvpStatus === 'coming' ? {
            vegan: dietaryVegan,
            glutenFree: dietaryGlutenFree,
            lactoseFree: dietaryLactoseFree,
            allergies: dietaryAllergies || null,
            misc: dietaryMisc || null,
          } : undefined,
          additionalGuests: rsvpStatus === 'coming' ? additionalGuests.map(ag => ({
            name: ag.name,
            type: ag.type,
            age: ag.age,
          })) : undefined,
          comments: comments || null,
        },
      })
    } catch (err) {
      // Don't fail the save if notification fails
      console.error('Failed to send RSVP notification:', err)
    }
  }

  const handleSave = async () => {
    setSaving(true)

    try {
      // Set session variable for changelog tracking
      try {
        await supabase.rpc('exec_sql', {
          query: "SELECT set_config('app.changed_by', 'guest', false)",
        })
      } catch {
        // Ignore if function doesn't exist
      }

      // Update guest record
      const { error } = await supabase
        .from('guests')
        .update({
          rsvp_status: rsvpStatus,
          dietary_vegan: dietaryVegan,
          dietary_gluten_free: dietaryGlutenFree,
          dietary_lactose_free: dietaryLactoseFree,
          dietary_allergies: dietaryAllergies || null,
          dietary_misc: dietaryMisc || null,
          comments: comments || null,
          email: email || null,
        })
        .eq('id', guest.id)

      if (error) throw error

      // Send email notification (fire and forget)
      sendRsvpNotification()

      notifications.show({
        title: 'Vielen Dank für Deine Rückmeldung!',
        message:
          rsvpStatus === 'coming'
            ? 'Deine Zusage wurde gespeichert.'
            : 'Deine Absage wurde gespeichert.',
        color: 'green',
        autoClose: 5000,
      })

      onUpdate?.()
    } catch (err) {
      console.error('Error saving RSVP:', err)
      notifications.show({
        title: 'Fehler',
        message: 'Ein Fehler ist aufgetreten',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  // Check if there's already a plus_one (regardless of who added it)
  const hasPlusOne = additionalGuests.some((ag) => ag.type === 'plus_one')
  // Check if there are any additional guests (plus_one or children)
  const hasAdditionalGuests = additionalGuests.length > 0

  return (
    <Stack gap="lg">
      <Text ta="center" size="xl" fw={600} c="golden">
        {content?.ui.Rückmeldung || 'Rückmeldung'}
      </Text>

      {/* RSVP Status Toggle */}
      <SegmentedControl
        value={rsvpStatus}
        onChange={(value) => setRsvpStatus(value as typeof rsvpStatus)}
        fullWidth
        size="lg"
        color="golden"
        data={[
          {
            value: 'coming',
            label: (
              <Group gap="xs" justify="center" wrap="nowrap">
                <IconCheck size={16} />
                <Box className={styles.segmentedControlLabel}>
                  {hasAdditionalGuests ? 'Wir kommen!' : 'Ich komme!'}
                </Box>
              </Group>
            ),
          },
          {
            value: 'not_coming',
            label: (
              <Group gap="xs" justify="center" wrap="nowrap">
                <IconX size={16} />
                <Box className={styles.segmentedControlLabel}>
                  {hasAdditionalGuests
                    ? 'Wir können leider nicht kommen'
                    : 'Ich kann leider nicht kommen'}
                </Box>
              </Group>
            ),
          },
        ]}
        className={styles.segmentedControl}
      />

      {/* Conditional sections only when accepting */}
      <Collapse in={rsvpStatus === 'coming'}>
        <Group wrap="nowrap" px="xl">
          <Stack gap="md" flex={1}>
            {/* Dietary Restrictions */}
            <Stack gap="sm">
              <Text size="md" fw={600} c="golden">
                {content?.rsvp.dietaryTitle || 'Besondere Ernährungswünsche'}
              </Text>

              <Checkbox
                label={content?.rsvp.dietaryVegan || 'Vegan'}
                checked={dietaryVegan}
                onChange={(e) => setDietaryVegan(e.currentTarget.checked)}
              />

              <Checkbox
                label={content?.rsvp.dietaryGlutenFree || 'Glutenfrei'}
                checked={dietaryGlutenFree}
                onChange={(e) => setDietaryGlutenFree(e.currentTarget.checked)}
              />

              <Checkbox
                label={content?.rsvp.dietaryLactoseFree || 'Laktosefrei'}
                checked={dietaryLactoseFree}
                onChange={(e) => setDietaryLactoseFree(e.currentTarget.checked)}
              />

              <TextInput
                label={content?.rsvp.dietaryAllergiesLabel || 'Allergien'}
                placeholder={content?.rsvp.dietaryAllergiesPlaceholder || 'z.B. Nüsse, Meeresfrüchte'}
                value={dietaryAllergies}
                onChange={(e) => setDietaryAllergies(e.currentTarget.value)}
              />

              <TextInput
                label={content?.rsvp.dietaryMiscLabel || 'Sonstiges'}
                placeholder={content?.rsvp.dietaryMiscPlaceholder || 'Weitere Ernährungswünsche'}
                value={dietaryMisc}
                onChange={(e) => setDietaryMisc(e.currentTarget.value)}
              />
            </Stack>

            {/* Additional Guests Section */}
            <Stack gap="sm">
              <Text size="md" fw={600} c="golden">
                {content?.rsvp.additionalGuestsTitle || 'Zusätzliche Gäste'}
              </Text>

              {/* All Additional Guests (guest and admin added together) */}
              {additionalGuests.length > 0 ? (
                <Stack gap="xs">
                  {additionalGuests.map((ag) => (
                    <Group key={ag.id} justify="space-between" wrap="nowrap">
                      <Group gap="xs" style={{ flex: 1 }}>
                        <Badge size="sm" color={ag.type === 'child' ? 'blue' : 'cyan'}>
                          {ag.type === 'child' ? 'Kind' : 'Begleitperson'}
                        </Badge>
                        <Text size="sm">
                          {ag.name} {ag.age !== null ? `(${ag.age})` : ''}
                        </Text>
                      </Group>
                      <Group gap="xs" wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          size="sm"
                          onClick={() => {
                            setEditingGuest(ag)
                            if (ag.type === 'plus_one') {
                              setPlusOneName(ag.name)
                              setPlusOneModalOpen(true)
                            } else {
                              setChildName(ag.name)
                              setChildAge(ag.age || undefined)
                              setChildModalOpen(true)
                            }
                          }}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => {
                            setGuestToDelete({ id: ag.id, name: ag.name })
                            setDeleteModalOpen(true)
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text size="sm" c="dimmed" ta="center" py="xs">
                  {content?.rsvp.noAdditionalGuests || 'Noch keine zusätzlichen Gäste hinzugefügt'}
                </Text>
              )}

              {/* Add Buttons */}
              <Group gap="xs">
                <Button
                  size="sm"
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setPlusOneModalOpen(true)}
                  disabled={hasPlusOne}
                >
                  {content?.rsvp.addPlusOne || '+1 hinzufügen'}
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setChildModalOpen(true)}
                >
                  {content?.rsvp.addChild || 'Kind hinzufügen'}
                </Button>
              </Group>
            </Stack>
          </Stack>
          <Center flex={1}>
            <Image
              ml="15%"
              src="/assets/scribe.png"
              alt="Mittelalterliche Schreiber"
              height={'auto'}
              maw={'70%'}
              fit="contain"
              radius="sm"
              flex={1}
              className={styles.flourish1}
              visibleFrom="md"
              opacity={0.7}
            />
          </Center>
        </Group>
      </Collapse>

      {/* Email (optional, always visible) */}
      <TextInput
        label={content?.rsvp.emailLabel || 'E-Mail-Adresse (optional)'}
        placeholder={content?.rsvp.emailPlaceholder || 'Für eventuelle Updates'}
        description={content?.rsvp.emailDescription || 'Falls wir Dich über Neuigkeiten informieren möchten'}
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
        type="email"
      />

      {/* Comments (always visible) */}
      <Textarea
        label={content?.rsvp.commentsLabel || 'Kommentare'}
        placeholder={content?.rsvp.commentsPlaceholder || 'Weitere Anmerkungen oder Fragen'}
        value={comments}
        onChange={(e) => setComments(e.currentTarget.value)}
        rows={4}
      />

      {/* Save Button */}
      <Button
        fullWidth
        size="lg"
        leftSection={<IconDeviceFloppy size={20} />}
        onClick={handleSave}
        loading={saving}
        className={styles.saveButton}
      >
        {content?.ui.Speichern || 'Speichern'}
      </Button>

      {/* Plus One Modal */}
      <Modal
        opened={plusOneModalOpen}
        onClose={() => {
          setPlusOneModalOpen(false)
          setPlusOneName('')
          setEditingGuest(null)
        }}
        title={editingGuest ? 'Begleitperson bearbeiten' : '+1 hinzufügen'}
        centered
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (plusOneName.trim()) {
              handleAddPlusOne()
            }
          }}
        >
          <Stack>
            <TextInput
              label="Name der Begleitperson"
              placeholder="Name eingeben"
              value={plusOneName}
              onChange={(e) => setPlusOneName(e.currentTarget.value)}
              required
              autoFocus
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                type="button"
                onClick={() => {
                  setPlusOneModalOpen(false)
                  setPlusOneName('')
                  setEditingGuest(null)
                }}
              >
                {content?.ui.Abbrechen || 'Abbrechen'}
              </Button>
              <Button type="submit" disabled={!plusOneName.trim()}>
                {editingGuest ? 'Aktualisieren' : 'Hinzufügen'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Child Modal */}
      <Modal
        opened={childModalOpen}
        onClose={() => {
          setChildModalOpen(false)
          setChildName('')
          setChildAge(undefined)
          setEditingGuest(null)
        }}
        title={editingGuest ? 'Kind bearbeiten' : 'Kind hinzufügen'}
        centered
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (childName.trim() && childAge !== undefined) {
              handleAddChild()
            }
          }}
        >
          <Stack>
            <TextInput
              label="Name des Kindes"
              placeholder="Name eingeben"
              value={childName}
              onChange={(e) => setChildName(e.currentTarget.value)}
              required
              autoFocus
            />
            <NumberInput
              label="Alter"
              placeholder="Alter eingeben"
              value={childAge}
              onChange={(val) => setChildAge(val as number)}
              min={0}
              max={17}
              required
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                type="button"
                onClick={() => {
                  setChildModalOpen(false)
                  setChildName('')
                  setChildAge(undefined)
                  setEditingGuest(null)
                }}
              >
                {content?.ui.Abbrechen || 'Abbrechen'}
              </Button>
              <Button type="submit" disabled={!childName.trim() || childAge === undefined}>
                {editingGuest ? 'Aktualisieren' : 'Hinzufügen'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setGuestToDelete(null)
        }}
        title="Gast entfernen"
        centered
      >
        <Stack>
          <Text>
            Möchten Sie {guestToDelete?.name || ''} wirklich entfernen?
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setDeleteModalOpen(false)
                setGuestToDelete(null)
              }}
            >
              {content?.ui.Abbrechen || 'Abbrechen'}
            </Button>
            <Button color="red" onClick={handleDeleteGuest}>
              Entfernen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
